"use client"

import type React from "react"

import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { useMobile } from "@/hooks/use-mobile"
import { useHeicImage } from "@/hooks/use-heic-image"
import { useProgressiveImage } from "@/hooks/use-progressive-image"
import { ImageCountBadge } from "@/components/ui/image-count-badge"
import { GalleryOpenRefContext } from "@/components/chat/gallery-open-context"
import { DismissableLayerBranch } from "@radix-ui/react-dismissable-layer"
import { isProbablyVideoUrl } from "@/lib/gallery-media"
import { isHeicUrl } from "@/lib/image-url-helper"

export type GalleryImage = {
  id: string
  url: string
  alt: string
  /**
   * The **byte-identical** URL the surface that opened this modal already
   * painted for this photo (the listing galleries paint `gallerySlideUrl`, i.e.
   * the `CARD` preset). Rendered as the first frame — out of the browser cache,
   * for 0 bytes — while `url`, the full original this viewer exists to show,
   * downloads underneath and replaces it once decoded.
   *
   * CALLER-SUPPLIED ON PURPOSE, never derived in here: the whole win is that
   * the string matches a request the browser has already served, and only the
   * caller knows which preset it asked for. A preset chosen inside this modal
   * would be a fourth URL for the same photo — a guaranteed cache miss plus one
   * more billed variant (`lib/images/presets.ts` header). Omit it and the
   * viewer behaves exactly as it did before: one stage, straight to `url`.
   */
  previewUrl?: string
}

export function ImageGalleryModal({
  images,
  isOpen,
  onCloseAction,
  initialIndex = 0,
  restoreScrollOnClose = true,
}: {
  images: GalleryImage[]
  isOpen: boolean
  onCloseAction: () => void
  initialIndex?: number
  restoreScrollOnClose?: boolean
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [scale, setScale] = useState(1)
  const [panning, setPanning] = useState(false)
  const [mouseSwiping, setMouseSwiping] = useState(false)
  const [pointX, setPointX] = useState(0)
  const [pointY, setPointY] = useState(0)
  const [start, setStart] = useState({ x: 0, y: 0 })
  const [hideZoomHint, setHideZoomHint] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const imageRef = useRef<HTMLDivElement>(null)
  const imageContentRef = useRef<HTMLImageElement>(null)
  const initialDistance = useRef<number | null>(null)
  const swipeStart = useRef<{ x: number; y: number } | null>(null)
  const swipeActive = useRef(false)
  const mouseSwipeStart = useRef<{ x: number; y: number } | null>(null)
  const mouseSwipeActive = useRef(false)
  const wheelNavigateAt = useRef(0)
  const scrollYRef = useRef(0)
  const activeIsVideoRef = useRef(false)
  const galleryContext = useContext(GalleryOpenRefContext)
  const galleryOpenRef = galleryContext?.galleryOpenRef ?? null
  const isZoomed = scale > 1
  const isMobile = useMobile()

  const wheelActionsRef = useRef({
    zoomIn: () => setScale((prev) => Math.min(prev + 0.2, 5)),
    zoomOut: () => {
      setScale((prev) => {
        const nextScale = Math.max(prev - 0.2, 1)
        if (nextScale <= 1) {
          setPointX(0)
          setPointY(0)
        }
        return nextScale
      })
    },
    next: () => setCurrentIndex((prev) => Math.min(prev + 1, images.length - 1)),
    prev: () => setCurrentIndex((prev) => Math.max(prev - 1, 0)),
    isZoomed,
    imagesLength: images.length,
  })
  wheelActionsRef.current = {
    zoomIn: () => setScale((prev) => Math.min(prev + 0.2, 5)),
    zoomOut: () => {
      setScale((prev) => {
        const nextScale = Math.max(prev - 0.2, 1)
        if (nextScale <= 1) {
          setPointX(0)
          setPointY(0)
        }
        return nextScale
      })
    },
    next: () => setCurrentIndex((prev) => Math.min(prev + 1, images.length - 1)),
    prev: () => setCurrentIndex((prev) => Math.max(prev - 1, 0)),
    isZoomed,
    imagesLength: images.length,
  }

  const activeImage = images[currentIndex] ?? null
  const activeGalleryUrl = activeImage?.url ?? ""
  const activeIsVideo = isProbablyVideoUrl(activeGalleryUrl)
  activeIsVideoRef.current = activeIsVideo
  // `enabled: isOpen` on BOTH funnels below. This component is mounted
  // unconditionally by both listing galleries whenever the listing has photos,
  // and the `if (!isOpen) return null` guard lives far below — hooks run
  // before it, by the rules of hooks. Without the gate, every listing-detail
  // pageview fetched the untouched original (and transcoded a HEIC) for a
  // viewer nobody opened, twice over, since the two responsive galleries are
  // toggled with Tailwind `hidden` and BOTH mount. No visible symptom; it
  // shows up only in the network panel and the bill.
  const { displaySrc, isConverting } = useHeicImage(activeIsVideo ? "" : activeGalleryUrl, {
    enabled: isOpen,
  })
  /** The full original — what this viewer exists to show. Unchanged by the two-stage load below. */
  const fullSrc = displaySrc || activeGalleryUrl || "/placeholder.svg"
  /**
   * Two cases where the caller's preview is dropped even when supplied:
   *
   * - **video** — `activeGalleryUrl` is played by a `<video>`, and an image
   *   upgrade in front of it is meaningless;
   * - **HEIC** — `useHeicImage` transcodes the upload and hands back an
   *   in-memory `blob:`. The CDN has never seen that picture at any preset, so
   *   there is no cached first frame to show and nothing to upgrade TO: the
   *   blob IS the final image. Bolting a second stage onto a one-stage path
   *   would only paint a `render/image` URL of a `.heic` that no imgproxy can
   *   decode. Guarded here rather than at each call site so a caller that
   *   forgets cannot break HEIC listings.
   */
  const previewSrc =
    activeIsVideo || isHeicUrl(activeGalleryUrl) ? undefined : activeImage?.previewUrl
  const progressive = useProgressiveImage(fullSrc, previewSrc, { enabled: isOpen })
  const imageUrls = useMemo(() => images.map((image) => image.url), [images])

  useEffect(() => {
    if (!isOpen) return
    setCurrentIndex(initialIndex)
  }, [initialIndex, isOpen])

  useEffect(() => {
    if (!isOpen) return
    setScale(1)
    setPointX(0)
    setPointY(0)
    setPanning(false)
    setMouseSwiping(false)
    mouseSwipeStart.current = null
    mouseSwipeActive.current = false
    setHideZoomHint(false)
    setImageLoaded(false)
  }, [currentIndex, isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseAction()
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, onCloseAction])

  useEffect(() => {
    if (!isOpen) return
    const html = document.documentElement
    const body = document.body
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow
    const prevBodyPosition = body.style.position
    const prevBodyTop = body.style.top
    const prevBodyWidth = body.style.width
    const prevBodyTouchAction = body.style.touchAction
    scrollYRef.current = window.scrollY || window.pageYOffset || 0
    html.style.overflow = "hidden"
    body.style.overflow = "hidden"
    if (restoreScrollOnClose) {
      body.style.position = "fixed"
      body.style.top = `-${scrollYRef.current}px`
      body.style.width = "100%"
      body.style.touchAction = "none"
    }
    return () => {
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
      body.style.position = prevBodyPosition
      body.style.top = prevBodyTop
      body.style.width = prevBodyWidth
      body.style.touchAction = prevBodyTouchAction
      if (restoreScrollOnClose) {
        const savedY = scrollYRef.current
        window.setTimeout(() => window.scrollTo(0, savedY), 0)
      }
    }
  }, [isOpen, restoreScrollOnClose])

  useEffect(() => {
    if (isZoomed) setHideZoomHint(true)
  }, [isZoomed])

  useEffect(() => {
    if (!galleryContext) return
    const { galleryOpenRef: ref, setGalleryOpen } = galleryContext
    if (isOpen) {
      ref.current = true
      setGalleryOpen(true)
      return () => {
        setGalleryOpen(false)
        queueMicrotask(() => { ref.current = false })
      }
    }
    ref.current = false
    setGalleryOpen(false)
  }, [isOpen, galleryContext])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: WheelEvent) => {
      const el = e.target as HTMLElement
      if (!el?.closest?.("[data-image-gallery]")) return
      const absDeltaX = Math.abs(e.deltaX)
      const absDeltaY = Math.abs(e.deltaY)
      const { zoomIn, zoomOut, next, prev, isZoomed: iz, imagesLength: len } = wheelActionsRef.current
      if (activeIsVideoRef.current) {
        if (!iz && len > 1) {
          const now = Date.now()
          const horizontal = absDeltaX > absDeltaY && absDeltaX > 35
          if (horizontal && now - wheelNavigateAt.current > 250) {
            wheelNavigateAt.current = now
            e.preventDefault()
            e.stopPropagation()
            if (e.deltaX > 0) next()
            else prev()
          }
        }
        return
      }
      if (absDeltaY >= absDeltaX) {
        e.preventDefault()
        e.stopPropagation()
        if (e.deltaY < 0) zoomIn()
        else zoomOut()
        return
      }
      if (!iz && len > 1) {
        const now = Date.now()
        const horizontal = absDeltaX > absDeltaY && absDeltaX > 35
        if (horizontal && now - wheelNavigateAt.current > 250) {
          wheelNavigateAt.current = now
          e.preventDefault()
          e.stopPropagation()
          if (e.deltaX > 0) next()
          else prev()
        }
      }
    }
    document.addEventListener("wheel", handler, { passive: false, capture: true })
    return () => document.removeEventListener("wheel", handler)
  }, [isOpen])

  const next = () => {
    if (images.length === 0) return
    setCurrentIndex((prev) => Math.min(prev + 1, images.length - 1))
  }
  const prev = () => {
    if (images.length === 0) return
    setCurrentIndex((prev) => Math.max(prev - 1, 0))
  }
  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, 5))
  const handleZoomOut = () => {
    setScale((prev) => {
      const nextScale = Math.max(prev - 0.2, 1)
      if (nextScale <= 1) {
        setPointX(0)
        setPointY(0)
      }
      return nextScale
    })
  }

  const calculateBounds = (newX: number, newY: number) => {
    if (!imageContentRef.current || !imageRef.current) return { x: newX, y: newY }

    const containerWidth = imageRef.current.clientWidth
    const containerHeight = imageRef.current.clientHeight
    const imageWidth = imageContentRef.current.naturalWidth
    const imageHeight = imageContentRef.current.naturalHeight

    const scaledImageWidth = imageWidth * scale
    const scaledImageHeight = imageHeight * scale

    const maxX = Math.max(0, (scaledImageWidth - containerWidth) / (2 * scale))
    const maxY = Math.max(0, (scaledImageHeight - containerHeight) / (2 * scale))

    return {
      x: Math.min(maxX, Math.max(-maxX, newX)),
      y: Math.min(maxY, Math.max(-maxY, newY)),
    }
  }

  const handleDoubleTap = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation()
    if (activeIsVideoRef.current) return

    if (isZoomed) {
      setScale(1)
      setPointX(0)
      setPointY(0)
    } else {
      setScale(2.5)
    }
  }

  const getDistanceBetweenTouches = (e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - e.touches[1].clientX
    const dy = e.touches[0].clientY - e.touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (activeIsVideoRef.current && e.touches.length > 1) {
      return
    }
    if (e.touches.length === 2) {
      e.stopPropagation()
      e.preventDefault()
      initialDistance.current = getDistanceBetweenTouches(e)
    } else if (e.touches.length === 1 && isZoomed) {
      e.stopPropagation()
      e.preventDefault()
      setPanning(true)
      setStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      })
    } else if (e.touches.length === 1 && !isZoomed) {
      swipeStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
      swipeActive.current = false
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (
      e.touches.length === 2 &&
      initialDistance.current &&
      !activeIsVideoRef.current
    ) {
      e.stopPropagation()
      e.preventDefault()

      const currentDistance = getDistanceBetweenTouches(e)
      const newScale = Math.max(1, Math.min(5, scale * (currentDistance / initialDistance.current)))
      initialDistance.current = currentDistance
      setScale(newScale)
      if (newScale <= 1) {
        setPointX(0)
        setPointY(0)
      }
    } else if (e.touches.length === 1 && panning && isZoomed) {
      e.stopPropagation()
      e.preventDefault()
      const dx = e.touches[0].clientX - start.x
      const dy = e.touches[0].clientY - start.y

      const newPointX = pointX + dx / scale
      const newPointY = pointY + dy / scale

      const bounded = calculateBounds(newPointX, newPointY)
      setPointX(bounded.x)
      setPointY(bounded.y)

      setStart({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      })
    } else if (e.touches.length === 1 && !isZoomed && swipeStart.current) {
      const dx = e.touches[0].clientX - swipeStart.current.x
      const dy = e.touches[0].clientY - swipeStart.current.y
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        swipeActive.current = true
        e.stopPropagation()
        e.preventDefault()
      }
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isZoomed) {
      e.stopPropagation()
    }
    initialDistance.current = null
    setPanning(false)
    if (!isZoomed && swipeStart.current) {
      const touch = (e.changedTouches && e.changedTouches[0]) || null
      if (touch) {
        const dx = touch.clientX - swipeStart.current.x
        const dy = touch.clientY - swipeStart.current.y
        const isHorizontal = Math.abs(dx) > Math.abs(dy)
        if (swipeActive.current && isHorizontal && Math.abs(dx) > 50) {
          if (dx < 0) next()
          else prev()
        }
      }
    }
    swipeStart.current = null
    swipeActive.current = false
  }

  const handleWheel = (e: React.WheelEvent) => {
    if (activeIsVideoRef.current) {
      const absDeltaX = Math.abs(e.deltaX)
      const absDeltaY = Math.abs(e.deltaY)
      if (!isZoomed && images.length > 1) {
        const now = Date.now()
        const horizontal = absDeltaX > absDeltaY && absDeltaX > 35
        if (horizontal && now - wheelNavigateAt.current > 250) {
          wheelNavigateAt.current = now
          e.stopPropagation()
          e.preventDefault()
          if (e.deltaX > 0) next()
          else prev()
        }
      }
      return
    }
    const absDeltaX = Math.abs(e.deltaX)
    const absDeltaY = Math.abs(e.deltaY)
    if (absDeltaY >= absDeltaX) {
      e.stopPropagation()
      e.preventDefault()
      if (e.deltaY < 0) handleZoomIn()
      else handleZoomOut()
      return
    }
    if (!isZoomed && images.length > 1) {
      const now = Date.now()
      const horizontal = absDeltaX > absDeltaY && absDeltaX > 35
      if (horizontal && now - wheelNavigateAt.current > 250) {
        wheelNavigateAt.current = now
        e.stopPropagation()
        e.preventDefault()
        if (e.deltaX > 0) next()
        else prev()
      }
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (activeIsVideoRef.current) {
      if (images.length > 1) {
        mouseSwipeStart.current = { x: e.clientX, y: e.clientY }
        mouseSwipeActive.current = false
        setMouseSwiping(true)
      }
      return
    }
    if (isZoomed) {
      e.stopPropagation()
      setPanning(true)
      setStart({
        x: e.clientX,
        y: e.clientY,
      })
      return
    }
    if (images.length > 1) {
      mouseSwipeStart.current = { x: e.clientX, y: e.clientY }
      mouseSwipeActive.current = false
      setMouseSwiping(true)
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (panning && isZoomed) {
      e.stopPropagation()
      const dx = e.clientX - start.x
      const dy = e.clientY - start.y

      const newPointX = pointX + dx / scale
      const newPointY = pointY + dy / scale

      const bounded = calculateBounds(newPointX, newPointY)
      setPointX(bounded.x)
      setPointY(bounded.y)

      setStart({
        x: e.clientX,
        y: e.clientY,
      })
      return
    }
    if (!isZoomed && mouseSwipeStart.current) {
      const dx = e.clientX - mouseSwipeStart.current.x
      const dy = e.clientY - mouseSwipeStart.current.y
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        mouseSwipeActive.current = true
      }
    }
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (isZoomed) {
      e.stopPropagation()
      setPanning(false)
      return
    }
    if (mouseSwipeStart.current) {
      const dx = e.clientX - mouseSwipeStart.current.x
      const dy = e.clientY - mouseSwipeStart.current.y
      const isHorizontal = Math.abs(dx) > Math.abs(dy)
      if (mouseSwipeActive.current && isHorizontal && Math.abs(dx) > 70) {
        if (dx < 0) next()
        else prev()
      }
    }
    mouseSwipeStart.current = null
    mouseSwipeActive.current = false
    setMouseSwiping(false)
  }

  const handleCloseZoomHint = (e: React.MouseEvent) => {
    e.stopPropagation()
    setHideZoomHint(true)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (e.target === e.currentTarget && !isZoomed) {
      onCloseAction()
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <DismissableLayerBranch>
      <div
        data-image-gallery
        className={cn(
        "fixed inset-0 z-9999 flex items-center justify-center overscroll-none",
        isMobile ? "bg-black" : "bg-black/70 backdrop-blur-sm",
        isZoomed ? "p-0" : "p-4",
      )}
        style={{
          pointerEvents: "auto",
          touchAction: isZoomed ? ("none" as const) : ("pan-y" as const),
          overscrollBehavior: "none" as const,
        }}
      onClick={handleBackdropClick}
      onWheel={handleWheel}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          onCloseAction()
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute top-4 right-4 z-100 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors touch-manipulation"
        aria-label="Close image"
      >
        <X className="h-6 w-6" />
      </button>

      {!isMobile && images.length > 1 && !isZoomed ? (
        <>
          {currentIndex > 0 ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev() }}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-110 w-12 h-12 cursor-pointer rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
          ) : null}
          {currentIndex < images.length - 1 ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next() }}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-110 w-12 h-12 cursor-pointer rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="h-7 w-7" />
            </button>
          ) : null}
        </>
      ) : null}

          {activeImage ? (
        <div className="absolute inset-0">
          {isMobile ? (
            activeIsVideo ? (
              <div className="absolute inset-0 bg-gradient-to-b from-neutral-900 via-neutral-950 to-black opacity-95" aria-hidden />
            ) : (
              <>
              {/*
                Blurred backdrop. Prefers the caller's preview: it is already in
                cache AND it is a fraction of the original's bytes, which is the
                right trade for a layer under `blur-3xl` — the blur destroys
                every frequency the extra pixels would have carried. Falls back
                to the original only when no preview was supplied, i.e. exactly
                today's behaviour for those callers.
              */}
              <img
                src={previewSrc ?? fullSrc}
                alt=""
                aria-hidden
                className={cn(
                  "h-full w-full object-cover blur-3xl scale-110 transition-opacity duration-300",
                  isConverting ? "opacity-0" : "opacity-70",
                )}
                draggable={false}
              />
              <div className="absolute inset-0 bg-black/20" />
              </>
            )
          ) : null}
        </div>
      ) : null}

      <div className="relative w-full h-full flex items-center justify-center select-none">
        <div
          ref={imageRef}
          className={cn(
            "relative w-full overflow-hidden rounded-2xl",
            !isMobile ? "max-w-3xl max-h-[85vh] aspect-square" : "max-w-md aspect-9/16",
          )}
          style={{
            cursor: activeIsVideo
              ? "default"
              : isZoomed
                ? (panning ? "grabbing" : "grab")
                : images.length > 1
                  ? (mouseSwiping ? "grabbing" : "grab")
                  : "default",
            touchAction: activeIsVideo ? "auto" : isZoomed ? "none" : "auto",
          }}
          onDoubleClick={handleDoubleTap}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {(isConverting || !imageLoaded) ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-10 w-10 animate-spin text-white/70" />
            </div>
          ) : null}

          <div
            className={cn(
              "relative h-full w-full flex items-center justify-center p-8 overflow-hidden rounded-2xl",
              activeIsVideo ? "touch-auto" : "touch-none select-none",
            )}
          >
            {activeIsVideo ? (
              <video
                key={activeGalleryUrl}
                src={activeGalleryUrl}
                controls
                playsInline
                className={cn(
                  "rounded-2xl shadow-2xl max-w-full max-h-full object-contain bg-black",
                  isConverting || !imageLoaded ? "opacity-0" : "opacity-100",
                )}
                onLoadedData={() => setImageLoaded(true)}
              />
            ) : (
              <img
                ref={imageContentRef}
                // Stage 1 = the caller's already-cached preset (paints in the
                // same frame as the tap), stage 2 = the original, swapped in
                // only after it has decoded. See `useProgressiveImage`.
                src={progressive.src}
                alt={activeImage?.alt || ""}
                className={cn(
                  "rounded-2xl shadow-2xl max-w-full max-h-full object-contain",
                  "transition-opacity duration-300 ease-out",
                  isConverting || !imageLoaded ? "opacity-0" : "opacity-100",
                )}
                style={{
                  transform: `scale(${scale}) translate(${pointX}px, ${pointY}px)`,
                  transition: panning ? "none" : "transform 0.3s ease-out",
                }}
                onLoad={() => setImageLoaded(true)}
                draggable={false}
              />
            )}
          </div>

          {!hideZoomHint && !isZoomed && !isMobile && !activeIsVideo ? (
            <div className="absolute left-4 bottom-16 z-40 animate-in fade-in-0 slide-in-from-bottom-2">
              <div className="bg-black/70 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-2 max-w-[240px] relative">
                <div className="flex items-center gap-1">
                  <span>Scroll to zoom!</span>
                </div>
                <button
                  type="button"
                  onClick={handleCloseZoomHint}
                  className="ml-2 p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Close zoom hint"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {images.length > 1 && !isZoomed ? (
        <ImageCountBadge
          className="absolute left-1/2 -translate-x-1/2 bottom-6"
          current={currentIndex + 1}
          total={imageUrls.length}
        />
      ) : null}
    </div>
    </DismissableLayerBranch>
  )

  if (typeof document === "undefined" || !document.body) return null
  return createPortal(modalContent, document.body)
}


